import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listShiftMessages, sendShiftMessage, raiseDispute, adminDeleteShiftMessage, reportShiftMessage } from "@/lib/messages.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { EndShiftEarlyDialog } from "@/components/EndShiftEarlyDialog";
import { DisputeSlaBadge } from "@/components/DisputeSlaBadge";

export const Route = createFileRoute("/_authenticated/shift/$id/notes")({
  head: () => ({ meta: [{ title: "Shift Chat — The Hospitality Reserve" }] }),
  component: ShiftNotes,
});

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_PREFIXES = ["image/", "application/pdf", "text/plain"];

type Msg = {
  id: string;
  sender_id: string;
  sender_role: string;
  body: string;
  created_at: string;
  attachment_path: string | null;
  attachment_name: string | null;
  attachment_mime: string | null;
  attachment_size_bytes: number | null;
  deleted_at: string | null;
  deleted_reason: string | null;
  scan_status: string | null;
  signed_url: string | null;
};

const REPORT_CATEGORIES = [
  { value: "abuse", label: "Abuse" },
  { value: "spam", label: "Spam" },
  { value: "harassment", label: "Harassment" },
  { value: "inappropriate", label: "Inappropriate content" },
  { value: "other", label: "Other" },
] as const;

function isAllowedMime(m: string) {
  return ALLOWED_PREFIXES.some((p) => m === p || m.startsWith(p));
}

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function ShiftNotes() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const load = useServerFn(listShiftMessages);
  const send = useServerFn(sendShiftMessage);
  const dispute = useServerFn(raiseDispute);
  const adminDelete = useServerFn(adminDeleteShiftMessage);
  const report = useServerFn(reportShiftMessage);

  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<"venue" | "bartender" | "admin" | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [shift, setShift] = useState<{ start_time: string; end_time: string; address: string | null } | null>(null);
  const [openDispute, setOpenDispute] = useState<{ created_at: string; escalated_at: string | null; resolved_at: string | null } | null>(null);
  const [draft, setDraft] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [raising, setRaising] = useState(false);
  const [modTarget, setModTarget] = useState<Msg | null>(null);
  const [modReason, setModReason] = useState("");
  const [modBusy, setModBusy] = useState(false);
  const [reportTarget, setReportTarget] = useState<Msg | null>(null);
  const [reportCategory, setReportCategory] = useState<typeof REPORT_CATEGORIES[number]["value"]>("abuse");
  const [reportDetails, setReportDetails] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [endEarlyOpen, setEndEarlyOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const refresh = async () => {
    const r = await load({ data: { shift_id: id } });
    if ("error" in r) { toast.error(r.error); navigate({ to: "/dashboard" }); return; }
    setRole(r.role);
    setMessages(r.messages as Msg[]);
    setShift(r.shift ? { start_time: r.shift.start_time, end_time: r.shift.end_time, address: (r.shift as { address?: string | null }).address ?? null } : null);
    setOpenDispute((r as { openDispute?: { created_at: string; escalated_at: string | null; resolved_at: string | null } | null }).openDispute ?? null);
    setLoading(false);
    setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight }), 50);
  };

  useEffect(() => { refresh(); }, [id]);

  useEffect(() => {
    const channel = supabase
      .channel(`shift_messages:${id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "shift_messages", filter: `shift_id=eq.${id}` },
        () => { void refresh(); },
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [id]);

  const handleFilePick = (f: File | null) => {
    if (!f) { setFile(null); return; }
    if (f.size > MAX_BYTES) { toast.error("Max file size is 10 MB."); return; }
    if (!isAllowedMime(f.type)) { toast.error("Allowed: images, PDF, plain text."); return; }
    setFile(f);
  };

  const handleSend = async () => {
    const body = draft.trim();
    if (!body && !file) return;
    setSending(true);
    try {
      let attachment: { path: string; name: string; mime: string; size_bytes: number } | undefined;
      if (file) {
        const { data: { session: __s } } = await supabase.auth.getSession(); const u = { user: __s?.user ?? null };
        const uid = u.user?.id;
        if (!uid) throw new Error("Sign in required.");
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 120);
        const path = `${id}/${uid}/${crypto.randomUUID()}-${safeName}`;
        const { error: upErr } = await supabase.storage.from("chat-attachments").upload(path, file, {
          contentType: file.type,
          upsert: false,
        });
        if (upErr) throw new Error(upErr.message);
        attachment = { path, name: file.name.slice(0, 255), mime: file.type, size_bytes: file.size };
      }
      const r = await send({ data: { shift_id: id, body, ...(attachment ? { attachment } : {}) } });
      if ("error" in r) throw new Error(r.error);
      setDraft(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setTimeout(() => { void refresh(); }, 250);
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed to send."); }
    finally { setSending(false); }
  };

  const handleRaise = async () => {
    const r = reason.trim();
    if (r.length < 5) { toast.error("Please add a short reason (5+ characters)."); return; }
    setRaising(true);
    try {
      const res = await dispute({ data: { shift_id: id, reason: r } });
      if ("error" in res) throw new Error(res.error);
      toast.success("Dispute raised — admins have been notified.");
      setDisputeOpen(false); setReason("");
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed."); }
    finally { setRaising(false); }
  };

  const handleModerate = async () => {
    if (!modTarget) return;
    const r = modReason.trim();
    if (r.length < 3) { toast.error("Reason required (3+ chars)."); return; }
    setModBusy(true);
    try {
      const res = await adminDelete({ data: { message_id: modTarget.id, reason: r } });
      if ("error" in res) throw new Error(res.error);
      toast.success("Message removed and logged.");
      setModTarget(null); setModReason("");
      await refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed."); }
    finally { setModBusy(false); }
  };

  const handleReport = async () => {
    if (!reportTarget) return;
    setReportBusy(true);
    try {
      const res = await report({ data: { message_id: reportTarget.id, category: reportCategory, details: reportDetails.trim() } });
      if ("error" in res) throw new Error(res.error);
      toast.success("Reported — admins will review.");
      setReportTarget(null); setReportDetails(""); setReportCategory("abuse");
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed."); }
    finally { setReportBusy(false); }
  };

  const canPost = role === "venue" || role === "bartender" || role === "admin";
  const canRaise = role === "venue" || role === "bartender";

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role={role === "admin" ? "admin" : role === "venue" ? "venue" : "bartender"} />
      <main className="mx-auto max-w-3xl px-6 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="tracking-wide-luxury text-accent">Live Chat</p>
            <h1 className="mt-3 font-display text-3xl">Venue ↔ Bartender</h1>
            {shift && (
              <p className="mt-2 text-xs text-muted-foreground">
                {new Date(shift.start_time).toLocaleString()} — {new Date(shift.end_time).toLocaleTimeString()} · {shift.address || "—"}
              </p>
            )}
            {openDispute && (
              <div className="mt-3 flex items-center gap-2">
                <DisputeSlaBadge
                  createdAt={openDispute.created_at}
                  escalatedAt={openDispute.escalated_at}
                  resolvedAt={openDispute.resolved_at}
                />
                <span className="text-[11px] text-muted-foreground">
                  Admin response SLA 48h · escalation is automatic after that.
                </span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Link to={role === "venue" ? "/venue" : role === "admin" ? "/admin" : "/bartender"}
              className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Back</Link>
            {canRaise && (
              <Button onClick={() => { setDisputeOpen(true); setReason(""); }} variant="outline"
                className="rounded-none border-destructive text-destructive hover:bg-destructive/10 tracking-wide-luxury">
                Raise dispute
              </Button>
            )}
            {canRaise && (
              <Button onClick={() => setEndEarlyOpen(true)} variant="outline"
                className="rounded-none border-destructive text-destructive hover:bg-destructive/10 tracking-wide-luxury">
                End shift early
              </Button>
            )}
          </div>
        </div>
        <span className="gold-divider mt-4" />

        {loading ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <div ref={listRef} className="mt-6 border border-border bg-card p-4 max-h-[500px] overflow-y-auto space-y-3">
              {messages.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No messages yet. Say hello — messages and files appear live for both sides.</p>
              ) : messages.map((m) => {
                const isImg = m.attachment_mime?.startsWith("image/");
                const deleted = !!m.deleted_at;
                return (
                  <div key={m.id} className={`border-l-2 pl-3 ${m.sender_role === "venue" ? "border-accent" : m.sender_role === "admin" ? "border-destructive" : "border-border"}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs tracking-wide-luxury text-muted-foreground">
                        {m.sender_role.toUpperCase()} · {new Date(m.created_at).toLocaleString()}
                      </p>
                      <div className="flex items-center gap-3">
                        {role !== null && !deleted && m.sender_id !== undefined && (
                          <button onClick={() => { setReportTarget(m); setReportCategory("abuse"); setReportDetails(""); }}
                            className="text-[10px] tracking-wide-luxury text-muted-foreground hover:text-destructive hover:underline">
                            Report
                          </button>
                        )}
                        {role === "admin" && !deleted && (
                          <button onClick={() => { setModTarget(m); setModReason(""); }}
                            className="text-[10px] tracking-wide-luxury text-destructive hover:underline">
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                    {m.body && <p className={`mt-1 text-sm whitespace-pre-wrap ${deleted ? "italic text-muted-foreground" : ""}`}>{m.body}</p>}
                    {m.attachment_path && !deleted && m.scan_status === "error" && (
                      <p className="mt-2 text-xs text-destructive">⚠ Attachment scan failed — file hidden pending admin review.</p>
                    )}
                    {m.attachment_path && !deleted && m.scan_status === "pending" && (
                      <p className="mt-2 text-xs text-muted-foreground">🛡 Attachment is being scanned for malware…</p>
                    )}
                    {m.attachment_path && !deleted && m.signed_url && (
                      <div className="mt-2">
                        {isImg ? (
                          <a href={m.signed_url} target="_blank" rel="noopener noreferrer"
                            aria-label={`Open image: ${m.attachment_name ?? "Uploaded image"}`}>
                            <img src={m.signed_url} alt={m.attachment_name ?? "Uploaded image"}
                              className="max-h-64 max-w-full border border-border" loading="lazy" />
                          </a>
                        ) : (
                          <a href={m.signed_url} target="_blank" rel="noopener noreferrer"
                            className="inline-block border border-border bg-background px-3 py-2 text-xs hover:border-accent">
                            📎 {m.attachment_name} <span className="text-muted-foreground">({m.attachment_size_bytes ? formatBytes(m.attachment_size_bytes) : ""})</span>
                          </a>
                        )}
                        <p className="mt-1 text-[10px] text-muted-foreground">✓ Scanned clean</p>
                      </div>
                    )}
                    {deleted && m.deleted_reason && role === "admin" && (
                      <p className="mt-1 text-[10px] text-muted-foreground">Moderator reason: {m.deleted_reason}</p>
                    )}
                  </div>
                );
              })}
            </div>

            {canPost ? (
              <div className="mt-4 space-y-2">
                <Textarea value={draft} onChange={(e) => setDraft(e.target.value.slice(0, 2000))} rows={3}
                  placeholder={role === "bartender" ? "Reply to the venue…" : "Add a note for the bartender (dress code, entrance, contact)…"}
                  className="rounded-none" />
                <div className="flex flex-wrap items-center gap-2">
                  <Input ref={fileRef} type="file" accept="image/*,application/pdf,text/plain"
                    aria-label="Attach a file"
                    onChange={(e) => handleFilePick(e.target.files?.[0] ?? null)}
                    className="rounded-none max-w-xs text-xs" />
                  {file && (
                    <span className="text-xs text-muted-foreground">
                      {file.name} ({formatBytes(file.size)})
                      <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                        className="ml-2 text-destructive hover:underline">remove</button>
                    </span>
                  )}
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-xs text-muted-foreground">{draft.length}/2000 · Max 10 MB · images, PDF, text</p>
                  <Button onClick={handleSend} disabled={sending || (!draft.trim() && !file)}
                    className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90 tracking-wide-luxury">
                    {sending ? "Sending…" : "Send"}
                  </Button>
                </div>
              </div>
            ) : (
              <p className="mt-4 text-xs text-muted-foreground">You can read this thread but cannot post.</p>
            )}
          </>
        )}
      </main>

      <Dialog open={disputeOpen} onOpenChange={(o) => { if (!o) { setDisputeOpen(false); setReason(""); } }}>
        <DialogContent className="rounded-none border-border bg-card">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Raise a dispute</DialogTitle>
            <DialogDescription>
              Admins will review the engagement, this thread, and the payment status. Please be specific.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value.slice(0, 2000))} rows={5}
            placeholder="What went wrong? Include times, who said what, and what outcome you're asking for."
            className="rounded-none" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDisputeOpen(false); setReason(""); }} className="rounded-none tracking-wide-luxury">Cancel</Button>
            <Button onClick={handleRaise} disabled={raising || reason.trim().length < 5}
              className="rounded-none bg-destructive text-white hover:bg-destructive/90 tracking-wide-luxury">
              {raising ? "Submitting…" : "Submit dispute"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!modTarget} onOpenChange={(o) => { if (!o) { setModTarget(null); setModReason(""); } }}>
        <DialogContent className="rounded-none border-border bg-card">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Remove message</DialogTitle>
            <DialogDescription>
              Soft-deletes the message and any attachment. A record is kept in the moderation audit log.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={modReason} onChange={(e) => setModReason(e.target.value.slice(0, 500))} rows={3}
            placeholder="Reason for removal (visible to admins only)…" className="rounded-none" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setModTarget(null); setModReason(""); }} className="rounded-none tracking-wide-luxury">Cancel</Button>
            <Button onClick={handleModerate} disabled={modBusy || modReason.trim().length < 3}
              className="rounded-none bg-destructive text-white hover:bg-destructive/90 tracking-wide-luxury">
              {modBusy ? "Removing…" : "Remove message"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reportTarget} onOpenChange={(o) => { if (!o) { setReportTarget(null); setReportDetails(""); setReportCategory("abuse"); } }}>
        <DialogContent className="rounded-none border-border bg-card">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Report message</DialogTitle>
            <DialogDescription>
              Flag this message for admin review. False or repeat reports may affect your account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label htmlFor="report-category" className="text-xs tracking-wide-luxury text-muted-foreground">Reason</label>
              <select id="report-category" value={reportCategory} onChange={(e) => setReportCategory(e.target.value as typeof reportCategory)}
                className="mt-1 w-full rounded-none border border-border bg-background px-3 py-2 text-sm">
                {REPORT_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <Textarea value={reportDetails} onChange={(e) => setReportDetails(e.target.value.slice(0, 1000))} rows={4}
              placeholder="Add context for the admin team (optional)…" className="rounded-none" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setReportTarget(null); setReportDetails(""); setReportCategory("abuse"); }} className="rounded-none tracking-wide-luxury">Cancel</Button>
            <Button onClick={handleReport} disabled={reportBusy}
              className="rounded-none bg-destructive text-white hover:bg-destructive/90 tracking-wide-luxury">
              {reportBusy ? "Reporting…" : "Submit report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <EndShiftEarlyDialog shiftId={id} open={endEarlyOpen} onOpenChange={setEndEarlyOpen} onSubmitted={refresh} />
    </div>
  );
}
